import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import { api, fmtINR, fmtNum, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, Ruler, Zap, Plus, Trash2, FileText, X, Printer } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import SlottedDiagram from "@/components/SlottedDiagram";

const SECTIONS = ["38 X 38", "40 X 40", "40 X 60", "40 X 80"];
const ANGLE_T = [1.0, 1.2, 1.5, 1.6, 1.8, 2.0, 2.5, 3.0];
const SHELF_T = [0.5, 0.55, 0.6, 0.65, 0.7, 0.8, 0.9, 1.0, 1.2, 1.5, 1.6, 1.8, 2.0];
const COVER_T = [0.5, 0.55, 0.6, 0.65, 0.7, 0.8, 1.0, 1.2];

const defaults = {
  height_in: 96,
  length_in: 36,
  depth_in: 18,
  shelves: 5,
  section: "40 X 40",
  angle_thickness_mm: 2.0,
  shelf_thickness_mm: 0.9,
  partitions: 0,
  partition_width_in: 18,   // auto-synced to rack depth
  partition_height_in: 12,
  partition_thickness_mm: 0.7,
  side_sheets: 0,
  back_sheets: 0,
  cover_thickness_mm: 0.7,
};

function Num({ label, unit, value, onChange, testid, hint, min = 0, max }) {
  return (
    <div>
      <label className="label-tech mb-2 flex items-center justify-between">
        <span>{label}</span>
        {unit && <span className="text-ink-faint normal-case tracking-normal mono">{unit}</span>}
      </label>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="input-tech mono"
        data-testid={testid}
      />
      {hint && <div className="text-[0.7rem] text-ink-muted mt-1.5">{hint}</div>}
    </div>
  );
}

function Select({ label, unit, value, onChange, options, testid, fmt = (v) => v }) {
  return (
    <div>
      <label className="label-tech mb-2 flex items-center justify-between">
        <span>{label}</span>
        {unit && <span className="text-ink-faint normal-case tracking-normal mono">{unit}</span>}
      </label>
      <select
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          onChange(typeof options[0] === "number" ? Number(v) : v);
        }}
        className="input-tech mono"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {fmt(o)}
          </option>
        ))}
      </select>
    </div>
  );
}

function summariseConfig(c) {
  const parts = [`${c.section} × ${c.angle_thickness_mm}mm`, `${c.length_in}"×${c.depth_in}"×${c.height_in}"`, `${c.shelves} shelves`];
  if (c.partitions) parts.push(`${c.partitions} part.`);
  if (c.back_sheets) parts.push(`${c.back_sheets} back`);
  if (c.side_sheets) parts.push(`${c.side_sheets} side`);
  return parts.join(" · ");
}

export default function Slotted() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDealer = user?.role === "dealer";

  const [cfg, setCfg] = useState(defaults);
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Multi-item cart
  const [items, setItems] = useState([]);
  const [editIdx, setEditIdx] = useState(null);     // index being edited (null = new)
  const [editQty, setEditQty] = useState(1);
  const [editLabel, setEditLabel] = useState("");

  // BOM modal
  const [bomOpen, setBomOpen] = useState(false);
  const [bomRows, setBomRows] = useState([]);
  const [bomBusy, setBomBusy] = useState(false);
  const canUseBom = user?.role === "admin" || user?.role === "employee";

  const [client, setClient] = useState({ name: "", company: "", contact: "", address: "", gstin: "" });
  const [notes, setNotes] = useState("");

  const update = (k) => (v) => setCfg((p) => ({ ...p, [k]: v }));

  // Auto-sync: partition width defaults to the rack depth (user can still override manually).
  useEffect(() => {
    setCfg((p) => ({ ...p, partition_width_in: p.depth_in }));
  }, [cfg.depth_in]);

  const compute = async () => {
    setComputing(true);
    try {
      const { data } = await api.post("/slotted/calculate", cfg);
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setComputing(false);
    }
  };

  const debRef = useRef(null);
  useEffect(() => {
    if (!result) return;
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/slotted/calculate", cfg);
        setResult(data);
      } catch (e) {
        /* silent */
      }
    }, 400);
    return () => debRef.current && clearTimeout(debRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  // ----- cart ops -----
  const addOrUpdate = () => {
    if (!result) { toast.error("Compute the configuration first"); return; }
    const item = { config: { ...cfg }, quantity: Number(editQty) || 1, label: editLabel, result };
    if (editIdx == null) {
      setItems((a) => [...a, item]);
      toast.success(`Added (qty ${item.quantity}) to quotation`);
    } else {
      setItems((a) => a.map((x, i) => (i === editIdx ? item : x)));
      toast.success(`Updated line ${editIdx + 1}`);
    }
    setEditIdx(null); setEditQty(1); setEditLabel("");
  };
  const editItem = (idx) => {
    const it = items[idx];
    setCfg({ ...it.config });
    setResult(it.result);
    setEditIdx(idx);
    setEditQty(it.quantity);
    setEditLabel(it.label || "");
  };
  const removeItem = (idx) => setItems((a) => a.filter((_, i) => i !== idx));

  const totals = items.reduce(
    (acc, it) => ({
      weight: acc.weight + (it.result?.total_weight_kg || 0) * it.quantity,
      price: acc.price + (it.result?.total_price || 0) * it.quantity,
    }),
    { weight: 0, price: 0 }
  );

  // ----- BOM (cutting list) -----
  const openBom = async () => {
    const payload = items.length
      ? { items: items.map((it) => ({ config: it.config, quantity: it.quantity, label: it.label })) }
      : result
      ? { items: [{ config: cfg, quantity: Number(editQty) || 1, label: editLabel }] }
      : null;
    if (!payload) { toast.error("Compute a configuration first"); return; }
    setBomBusy(true); setBomOpen(true);
    try {
      const { data } = await api.post("/slotted/bom", payload);
      setBomRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBomOpen(false);
    } finally {
      setBomBusy(false);
    }
  };

  const save = async () => {
    const linesToSave = items.length
      ? items
      : result
      ? [{ config: cfg, quantity: Number(editQty) || 1, label: editLabel, result }]
      : null;
    if (!linesToSave) { toast.error("Add at least one line to the quotation"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/slotted/quotations", {
        client, notes,
        items: linesToSave.map((it) => ({ config: it.config, quantity: it.quantity, label: it.label || "" })),
      });
      toast.success(`Slotted ${data.quote_number} saved`);
      navigate(`/slotted-quotations/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="Slotted Angle · Configurator"
        title="Slotted Angle Rack"
        subtitle="Configure each rack, add multiple types with quantities, then generate BOM or save as quotation."
      />

      <div className="p-8 grid grid-cols-1 xl:grid-cols-5 gap-px bg-line border border-line">
        {/* INPUT PANEL */}
        <section className="xl:col-span-2 bg-white p-6" data-testid="sa-inputs">
          <div className="flex items-center gap-2 mb-5">
            <Ruler size={18} className="text-brand" />
            <div className="label-tech">1 · Rack dimensions</div>
          </div>

          <div className="grid grid-cols-2 gap-5">
            <Num label="Height" unit="in" value={cfg.height_in} onChange={update("height_in")} testid="sa-height" min={12} max={144} />
            <Num label="Length" unit="in" value={cfg.length_in} onChange={update("length_in")} testid="sa-length" hint="12–60 in" min={12} max={60} />
            <Num label="Depth" unit="in" value={cfg.depth_in} onChange={update("depth_in")} testid="sa-depth" hint="6–36 in" min={6} max={36} />
            <Num label="Shelves" unit="count" value={cfg.shelves} onChange={update("shelves")} testid="sa-shelves" min={2} max={12} />
          </div>

          <div className="mt-6 pt-5 border-t border-line">
            <div className="label-tech mb-3">2 · Slotted angle</div>
            <div className="grid grid-cols-2 gap-5">
              <Select label="Section" value={cfg.section} onChange={update("section")} options={SECTIONS} testid="sa-section" />
              <Select label="Angle thickness" unit="mm" value={cfg.angle_thickness_mm} onChange={update("angle_thickness_mm")} options={ANGLE_T} fmt={(v) => `${v.toFixed(2)} mm`} testid="sa-angle-thick" />
            </div>
          </div>

          <div className="mt-6 pt-5 border-t border-line">
            <div className="label-tech mb-3">3 · Shelves</div>
            <Select label="Shelf thickness" unit="mm" value={cfg.shelf_thickness_mm} onChange={update("shelf_thickness_mm")} options={SHELF_T} fmt={(v) => `${v.toFixed(2)} mm`} testid="sa-shelf-thick" />
          </div>

          <div className="mt-6 pt-5 border-t border-line">
            <div className="label-tech mb-3">4 · Partitions (optional)</div>
            <div className="grid grid-cols-2 gap-5">
              <Num label="Count" unit="" value={cfg.partitions} onChange={update("partitions")} testid="sa-partitions" min={0} max={50} />
              <Select label="Part. thickness" unit="mm" value={cfg.partition_thickness_mm} onChange={update("partition_thickness_mm")} options={COVER_T} fmt={(v) => `${v.toFixed(2)} mm`} testid="sa-part-thick" />
              <Num label="Partition W" unit="in" value={cfg.partition_width_in} onChange={update("partition_width_in")} testid="sa-part-w" min={0} max={36} hint="auto = depth" />
              <Num label="Partition H" unit="in" value={cfg.partition_height_in} onChange={update("partition_height_in")} testid="sa-part-h" min={0} max={36} />
            </div>
          </div>

          <div className="mt-6 pt-5 border-t border-line">
            <div className="label-tech mb-3">5 · Back / side sheets (optional)</div>
            <div className="grid grid-cols-2 gap-5">
              <Num label="Back sheets" unit="count" value={cfg.back_sheets} onChange={update("back_sheets")} testid="sa-back-count" min={0} max={2} hint="0 or 1" />
              <Num label="Side sheets" unit="count" value={cfg.side_sheets} onChange={update("side_sheets")} testid="sa-side-count" min={0} max={2} hint="0, 1 or 2" />
              <Select label="Cover thickness" unit="mm" value={cfg.cover_thickness_mm} onChange={update("cover_thickness_mm")} options={COVER_T} fmt={(v) => `${v.toFixed(2)} mm`} testid="sa-cover-thick" />
            </div>
          </div>

          <button onClick={compute} disabled={computing} className="btn-primary w-full mt-6" data-testid="sa-calc-btn">
            <Zap size={14} /> {computing ? "Computing…" : "Compute rack"}
          </button>

          <div className="mt-7 pt-5 border-t border-line">
            <div className="label-tech mb-3">
              Client details <span className="text-ink-faint normal-case tracking-normal">(optional)</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className="input-tech col-span-2" placeholder="Company name" value={client.company} onChange={(e) => setClient({ ...client, company: e.target.value })} />
              <input className="input-tech" placeholder="Contact" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} />
              <input className="input-tech" placeholder="Phone / email" value={client.contact} onChange={(e) => setClient({ ...client, contact: e.target.value })} />
              <input className="input-tech col-span-2" placeholder="Address" value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} />
              <input className="input-tech col-span-2" placeholder="GSTIN" value={client.gstin} onChange={(e) => setClient({ ...client, gstin: e.target.value })} />
              <textarea className="input-tech col-span-2 h-20 py-2" placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
        </section>

        {/* RESULT + CART PANEL */}
        <section className="xl:col-span-3 bg-white p-6" data-testid="sa-result">
          <div className="flex items-center justify-between mb-5">
            <div className="label-tech">
              {editIdx == null ? "Preview" : `Editing line ${editIdx + 1}`}
              {computing && <span className="ml-2 text-brand">computing…</span>}
            </div>
            <div className="flex gap-2">
              {canUseBom && (
                <button onClick={openBom} disabled={!result && items.length === 0} className="btn-secondary" data-testid="sa-bom-btn">
                  <FileText size={14} /> BOM
                </button>
              )}
              <button onClick={save} disabled={saving || (items.length === 0 && !result)} className="btn-primary" data-testid="sa-save-btn">
                <Save size={14} /> {saving ? "Saving…" : "Save quotation"}
              </button>
            </div>
          </div>

          {/* CART */}
          <div className="panel-heavy mb-5" data-testid="sa-cart">
            <div className="px-4 py-2 border-b border-line bg-surfaceAlt flex items-center justify-between">
              <div className="label-tech">Quotation items ({items.length})</div>
              <div className="mono text-xs text-ink-muted">
                Total {fmtNum(totals.weight, 1)} kg · {fmtINR(totals.price)}
              </div>
            </div>
            {items.length === 0 ? (
              <div className="p-5 text-xs text-ink-muted italic">
                Compute a rack on the left, set quantity below and click <b>Add to quotation</b> to build a multi-item quote.
              </div>
            ) : (
              <table className="w-full grid-lines">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Config</th>
                    <th>Label</th>
                    <th className="num">Qty</th>
                    <th className="num">Wt (kg)</th>
                    <th className="num">Price</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className={editIdx === i ? "bg-orange-50" : ""} data-testid={`sa-cart-row-${i}`}>
                      <td className="mono num">{i + 1}</td>
                      <td className="text-xs">{summariseConfig(it.config)}</td>
                      <td className="text-xs">{it.label || "—"}</td>
                      <td className="num mono">{it.quantity}</td>
                      <td className="num mono">{fmtNum(it.result.total_weight_kg * it.quantity, 2)}</td>
                      <td className="num font-bold">{fmtINR(it.result.total_price * it.quantity)}</td>
                      <td className="flex gap-1 py-2">
                        <button onClick={() => editItem(i)} className="btn-ghost text-[0.65rem] py-0.5">Edit</button>
                        <button onClick={() => removeItem(i)} className="btn-danger text-[0.65rem] py-0.5"><Trash2 size={10} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {/* Add-to-cart row */}
            <div className="px-4 py-3 border-t border-line bg-white flex items-end gap-3">
              <div className="flex-1">
                <label className="label-tech text-[0.65rem] mb-1 block">Label (optional)</label>
                <input className="input-tech mono text-xs" placeholder="e.g. Ground-floor 96&quot; bank"
                       value={editLabel} onChange={(e) => setEditLabel(e.target.value)}
                       data-testid="sa-item-label" />
              </div>
              <div style={{ width: 96 }}>
                <label className="label-tech text-[0.65rem] mb-1 block">Quantity</label>
                <input type="number" min={1} value={editQty}
                       onChange={(e) => setEditQty(Number(e.target.value) || 1)}
                       className="input-tech mono text-xs" data-testid="sa-item-qty" />
              </div>
              <button onClick={addOrUpdate} disabled={!result} className="btn-secondary" data-testid="sa-item-add">
                <Plus size={14} /> {editIdx == null ? "Add to quotation" : "Update line"}
              </button>
              {editIdx != null && (
                <button onClick={() => { setEditIdx(null); setEditQty(1); setEditLabel(""); }} className="btn-ghost" data-testid="sa-item-cancel">
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* LIVE PREVIEW */}
          {!result ? (
            <div className="panel-heavy p-10 min-h-[220px] flex items-center justify-center bp-grid">
              <div className="text-center">
                <div className="mono text-6xl font-bold text-ink-faint mb-4">—</div>
                <div className="label-tech">Compute to see BOM & price</div>
              </div>
            </div>
          ) : (
            <div className="panel-heavy" data-testid="sa-spec">
              <div className="px-4 py-2 border-b border-line bg-surfaceAlt label-tech">Current configuration preview</div>

              {/* Grey SVG elevation diagram */}
              <SlottedDiagram cfg={cfg} />

              <table className="w-full grid-lines">
                <thead>
                  <tr>
                    <th>Component</th>
                    {!isDealer && <th>Size</th>}
                    <th className="num">T (mm)</th>
                    <th className="num">Approx unit wt</th>
                    <th className="num">Qty</th>
                    <th className="num">Total wt</th>
                  </tr>
                </thead>
                <tbody>
                  {result.components.map((c) => (
                    <tr key={c.component}>
                      <td className="font-bold uppercase tracking-wider text-xs">{c.component}</td>
                      {!isDealer && <td className="mono text-xs">{c.section}</td>}
                      <td className="num mono text-xs">{c.thickness_mm > 0 ? c.thickness_mm : "—"}</td>
                      <td className="num">{fmtNum(c.unit_weight_kg, 3)} kg</td>
                      <td className="num">{c.quantity}</td>
                      <td className="num font-bold">{fmtNum(c.total_weight_kg, 2)} kg</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="px-4 sm:px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-3 border-t-2 border-ink">
                <div>
                  <div className="label-tech">Rack weight</div>
                  <div className="mono text-2xl font-bold mt-1">{fmtNum(result.total_weight_kg, 2)} kg</div>
                </div>
                <div>
                  <div className="label-tech">Rate</div>
                  <div className="mono text-2xl font-bold mt-1">
                    ₹{parseFloat(Number(result.price_per_kg).toFixed(2))} / kg
                  </div>
                </div>
                <div>
                  <div className="label-tech">Rack price</div>
                  <div className="display font-black text-3xl text-brand mt-1">{fmtINR(result.total_price)}</div>
                </div>
              </div>

              {!isDealer && (
                <div className="px-6 py-4 border-t border-line bg-surfaceAlt">
                  <div className="label-tech mb-2">Formulas applied</div>
                  <ul className="text-xs text-ink-muted space-y-1 mono">
                    {result.components.filter((c) => c.note).map((c, i) => (
                      <li key={i}><span className="font-bold text-ink">{c.component}:</span> {c.note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {bomOpen && (
        <BomModal rows={bomRows} busy={bomBusy} client={client}
                  items={items.length ? items : (result ? [{ config: cfg, quantity: Number(editQty) || 1, label: editLabel }] : [])}
                  onClose={() => setBomOpen(false)} />
      )}
    </Layout>
  );
}


function BomModal({ rows, busy, items, client, onClose }) {
  const groups = Array.isArray(rows) && rows.length && rows[0] && Array.isArray(rows[0].lines)
    ? rows
    : (rows && rows.length
        ? [{ item_index: 1, label: "", summary: "", quantity: 1, total_pieces: rows.reduce((s,r)=>s+(r.quantity||0),0), lines: rows }]
        : []);
  const total_pieces = groups.reduce((s, g) => s + (g.total_pieces || 0), 0);
  useEffect(() => {
    document.body.classList.add("printing-bom");
    return () => document.body.classList.remove("printing-bom");
  }, []);
  const node = (
    <div className="pr-bom-overlay fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="sa-bom-modal">
      <div className="pr-bom-panel bg-white w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col border border-line shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-line bg-surfaceAlt no-print">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-brand" />
            <div className="mono font-bold uppercase tracking-wider text-sm">Bill of Materials — Fabricator Cutting List</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="btn-primary"><Printer size={14} /> Print / PDF</button>
            <button onClick={onClose} className="btn-ghost"><X size={14} /></button>
          </div>
        </div>

        <div className="pr-bom-body flex-1 overflow-y-auto p-6">
          {/* Company + client header (prints) */}
          <div className="print-only pb-3 border-b-2 border-black mb-4">
            <div className="flex items-center justify-between">
              <img src="/brand/adarsh-logo.png" alt="Adarsh Udyog" style={{ height: 44, objectFit: "contain" }} />
              <div className="text-center">
                <div className="font-black uppercase tracking-widest text-lg">SLOTTED ANGLE · BOM</div>
                <div className="text-[10px] mono">{client.company || client.name || "—"}{client.address ? ` · ${client.address}` : ""}</div>
              </div>
              <div className="text-[10px] text-right mono">
                <div>Date: {new Date().toLocaleDateString("en-IN")}</div>
                <div>All dims in MM</div>
              </div>
            </div>
          </div>

          <div className="text-xs mb-3 text-ink-muted">
            Sheet cutting list grouped by line item · <b>{groups.length}</b> line{groups.length === 1 ? "" : "s"} ·
            total <b className="text-ink">{total_pieces}</b> pieces to fabricate.
          </div>

          {busy ? (
            <div className="p-10 text-center label-tech">Building BOM…</div>
          ) : groups.length === 0 ? (
            <div className="p-10 text-center label-tech italic text-ink-muted">No BOM lines.</div>
          ) : (
            groups.map((g) => (
              <div key={g.item_index} className="mb-6 bom-group" data-testid={`sa-bom-group-${g.item_index}`}>
                <div className="flex items-baseline justify-between pb-1.5 mb-2 border-b border-ink">
                  <div className="mono font-bold uppercase tracking-wider text-sm text-ink">
                    Item {g.item_index}
                    {g.label ? <span className="ml-2 text-ink-muted normal-case tracking-normal">· {g.label}</span> : null}
                  </div>
                  <div className="text-[0.7rem] mono text-ink-muted">
                    {g.total_pieces} pieces
                  </div>
                </div>
                <div className="text-[0.75rem] mono text-ink-muted mb-2">{g.summary}</div>
                <div className="panel-heavy">
                  <table className="w-full grid-lines">
                    <thead className="bg-black text-white">
                      <tr>
                        <th className="text-left">Component</th>
                        <th className="text-left">Section / Ref</th>
                        <th className="num">T (mm)</th>
                        <th className="num">Width (mm)</th>
                        <th className="num">Length (mm)</th>
                        <th className="num">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.lines.map((r, i) => (
                        <tr key={i} data-testid={`sa-bom-row-${g.item_index}-${i}`}>
                          <td className="font-bold uppercase tracking-wider text-xs">{r.component}</td>
                          <td className="mono text-xs">{r.section}</td>
                          <td className="num mono text-xs">{r.thickness_mm > 0 ? r.thickness_mm : "—"}</td>
                          <td className="num mono">{r.width_mm > 0 ? r.width_mm : "—"}</td>
                          <td className="num mono">{r.length_mm > 0 ? r.length_mm : "—"}</td>
                          <td className="num font-bold">{r.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}

          {/* No further "included configs" section — each subsection already carries its own summary */}
        </div>
      </div>
    </div>
  );
  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
}
